import type { UserOut } from "@shop-ledger/shared-types";

/** Thin typed `localStorage` wrapper for the persisted auth session — same pattern as
 * `pos-pwa/src/lib/storage.ts`'s `AuthSession`, trimmed to just what this app needs (no
 * sync cursor, no printer preference — this app has neither). */

const AUTH_SESSION_KEY = "sl-dashboard-auth-session";

export interface AuthSession {
  accessToken: string;
  /** `null` only for an impersonation session established via `/impersonate?code=...`
   * (`ImpersonationConsumePage`) — those tokens are deliberately short-lived and
   * non-renewable (§9.1), so there is no refresh token to store. `ensureFreshToken()`
   * and `logout()` both branch on this. */
  refreshToken: string | null;
  /** ISO datetime the access token expires. `client: "dashboard"` logins get the
   * short-lived (15 min default) token per §2 — unlike `pos-pwa`, this app has no offline
   * exception, so an expired token should prompt a real re-login/refresh promptly rather
   * than being tolerated. */
  expiresAt: string;
  user: UserOut;
}

export function loadAuthSession(): AuthSession | null {
  const raw = localStorage.getItem(AUTH_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

export function saveAuthSession(session: AuthSession): void {
  localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
}

export function clearAuthSession(): void {
  localStorage.removeItem(AUTH_SESSION_KEY);
}
